import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { buildLangVariantTabs } from '../../Helpers/ReduxHelper'

interface LanguageVariantTab {
  label: string
  value: string
  showSuccessCheck?: boolean
}

interface AssetLanguageVariantsState {
  isBaseAsset: boolean | string | undefined
  langVariants: any[]
  langVariant: any | null
  langVariantDisable: boolean
  langVariantId: string | number | undefined
  langVariantTabs: LanguageVariantTab[]
  langVariantTab: string | number | undefined
  langVariantMarkDoneStatus: any[] | null
  langVariantAssetLanguages: string[] | null
  langVariantAvailableLanguages: string[] | null
  langWithKeyValue: any[]
}

const initialState: AssetLanguageVariantsState = {
  isBaseAsset: undefined,
  langVariants: [],
  langVariant: null,
  langVariantDisable: false,
  langVariantId: undefined,
  langVariantTabs: [],
  langVariantTab: undefined,
  langVariantMarkDoneStatus: null,
  langVariantAssetLanguages: null,
  langVariantAvailableLanguages: null,
  langWithKeyValue: [],
}

const assetLanguageVariantsSlice = createSlice({
  name: 'assetLanguageVariants',
  initialState,
  reducers: {
    setAssetLangVariants: (state, action: PayloadAction<any>) => {
      const {
        baseContentId,
        variantAssetLanguages,
        variantsData,
        contentDetails = {},
        metaCategory,
        contentId,
        languagesMasters,
      } = action.payload
      const { isBaseAsset } = contentDetails
      if (isBaseAsset === true || baseContentId === contentDetails?.contentId) {
        state.isBaseAsset = true
      } else if (
        isBaseAsset === false &&
        baseContentId &&
        baseContentId !== contentDetails?.contentId
      ) {
        state.isBaseAsset = false
      } else {
        state.isBaseAsset = undefined
      }
      state.langVariants = action.payload?.variantsData ?? []
      state.langVariantAssetLanguages = action.payload?.variantAssetLanguages ?? []
      state.langVariantAvailableLanguages = action.payload?.variantAssetLanguages ?? []

      const { tabs, remainingLanguages, languageOptions } = buildLangVariantTabs({
        baseContentId: contentDetails?.contentId,
        baseVersionId: contentId,
        metaCategory,
        variantAssetLanguages: variantAssetLanguages || [],
        variantsData: variantsData || [],
        markDoneStatus: [],
        languagesMasters,
      })
      state.langVariantTabs = tabs
      state.langVariantAvailableLanguages = remainingLanguages
      state.langWithKeyValue = languageOptions
    },
    setLangVariants: (state, action: PayloadAction<any[]>) => {
      state.langVariants = action.payload
    },
    setLangVariant: (state, action: PayloadAction<any | null>) => {
      state.langVariant = action.payload
    },
    setLangVariantId: (state, action: PayloadAction<string | number | undefined>) => {
      state.langVariantId = action.payload
      state.langVariant =
        state.langVariants.find((variant) => variant.contentDetails?._id === action.payload) || null
      if (state.langVariant) {
        state.langVariantDisable = state.langVariant?.contentDetails?.state !== 'CREATED'
      } else {
        state.langVariantDisable = false
      }
    },
    setLangVariantTabs: (state, action: PayloadAction<LanguageVariantTab[]>) => {
      state.langVariantTabs = action.payload
    },
    setLangVariantTab: (state, action: PayloadAction<string | number | undefined>) => {
      state.langVariantTab = action.payload
    },
    setLangVariantMarkDoneStatus: (state, action: PayloadAction<any | null>) => {
      const stateCopy = JSON.parse(JSON.stringify(state))
      const { markDoneStatus, baseVersionId, metaCategory, languagesMasters, baseContentId } =
        action.payload

      if (stateCopy.langVariants?.length) {
        state.langVariants = stateCopy.langVariants.map((variant: any) => {
          const updatedMetaCategories = variant.metaCategories?.map((item: any) => {
            let updatedStatus = item.markDoneStatus
            const isCreated = variant.contentDetails?.state === 'CREATED'
            const isBaseContent = baseContentId === variant.contentDetails?.contentId
            const isMatchingCategory = item.metaCategoryKey === metaCategory
            const isMatchingId =
              variant.contentDetails?._id === (markDoneStatus?.[0]?._id || baseVersionId)
            if ((isCreated || isBaseContent) && isMatchingCategory && isMatchingId) {
              updatedStatus = markDoneStatus[0].markDoneStatus
            }
            return {
              ...item,
              markDoneStatus: updatedStatus,
            }
          })
          return {
            ...variant,
            metaCategories: updatedMetaCategories,
          }
        })
      }

      state.langVariantMarkDoneStatus = markDoneStatus?.[0]?._id ? markDoneStatus : null

      const { tabs, remainingLanguages, languageOptions } = buildLangVariantTabs({
        baseContentId,
        baseVersionId,
        metaCategory,
        variantAssetLanguages: stateCopy.langVariantAssetLanguages || [],
        variantsData: stateCopy.langVariants || [],
        markDoneStatus: markDoneStatus || [],
        languagesMasters,
      })
      state.langVariantTabs = tabs
      state.langVariantAvailableLanguages = remainingLanguages
      state.langWithKeyValue = languageOptions
    },
    setLangVariantAssetLanguages: (state, action: PayloadAction<string[] | null>) => {
      state.langVariantAssetLanguages = action.payload
    },
    setLangVariantAvailableLanguages: (state, action: PayloadAction<string[] | null>) => {
      state.langVariantAvailableLanguages = action.payload
    },
    resetAssetLanguageVariantsByKeyState: (
      state,
      action: PayloadAction<{ key: keyof AssetLanguageVariantsState; value: any }>,
    ) => {
      state[action.payload.key] = action.payload.value
    },
    resetAssetLanguageVariantsState: () => initialState,
  },
})

export const {
  setAssetLangVariants,
  setLangVariants,
  setLangVariant,
  setLangVariantId,
  setLangVariantTabs,
  setLangVariantTab,
  setLangVariantMarkDoneStatus,
  setLangVariantAssetLanguages,
  setLangVariantAvailableLanguages,
  resetAssetLanguageVariantsByKeyState,
  resetAssetLanguageVariantsState,
} = assetLanguageVariantsSlice.actions

export default assetLanguageVariantsSlice

// -----------------
// Selectors
// -----------------

const assetLanguageVariantsSelector = (state: {
  assetLanguageVariants: AssetLanguageVariantsState
}) => state.assetLanguageVariants

export const selectIsBaseAsset = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.isBaseAsset,
)

export const selectLangVariants = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariants,
)

export const selectLangVariant = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariant,
)

export const selectLangVariantDisable = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantDisable,
)

export const selectLangVariantId = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantId,
)

export const selectLangVariantTabs = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantTabs,
)

export const selectLangVariantTab = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantTab,
)

export const selectLangVariantMarkDoneStatus = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantMarkDoneStatus,
)

export const selectLangVariantAvailableLanguages = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantAvailableLanguages,
)

export const selectLangVariantAssetLanguages = createSelector(
  assetLanguageVariantsSelector,
  (state) => state.langVariantAssetLanguages,
)
